/**
 * Migration wizard API client
 *
 * Issue: skill-bridge/media_app#6000
 *
 * 4 endpoints:
 *   - POST /v1/migration/chatwork/connect
 *   - POST /v1/migration/chatwork/preview
 *   - POST /v1/migration/chatwork/start
 *   - GET  /v1/migration/jobs/:id
 *
 * 加えて運用補助:
 *   - POST /v1/migration/jobs/:id/cancel
 *   - POST /v1/migration/jobs/:id/retry-deadletter
 *
 * 実装ポリシー:
 *   - JWT 更新を含む共通 authenticatedFetch を使う
 *   - error は `{ error: { code, message } }` 形式を仮定
 */

import { authenticatedFetch, resolveApiUrl } from "@/lib/api";

import type {
  ConnectResponse,
  MigrationJobPublic,
  PreviewResponse,
} from "./types";

export class MigrationApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "MigrationApiError";
  }
}

export interface MigrationApiOptions {
  /** absolute base URL (本番) もしくは "/api" 形式の prefix。default は `/api` */
  baseUrl?: string;
  /** Bearer token (組織スコープ JWT)。null/undefined のときは未認証 */
  authToken?: string | null;
  /** テスト用 fetcher 差し替え */
  fetcher?: typeof fetch;
}

export class MigrationApi {
  private readonly baseUrl: string;
  private readonly authToken: string | null;
  private readonly fetcher: typeof fetch;

  constructor(opts: MigrationApiOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? resolveApiUrl("")).replace(/\/$/, "");
    this.authToken = opts.authToken ?? null;
    this.fetcher = opts.fetcher ?? authenticatedFetch;
  }

  private async request<T>(
    method: "GET" | "POST",
    path: string,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;
    const url = `${this.baseUrl}${path}`;
    const res = await this.fetcher(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let parsed: unknown;
    try {
      const text = await res.text();
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const code =
        (parsed as { error?: { code?: string } } | null)?.error?.code ??
        "UNKNOWN_ERROR";
      const msg =
        (parsed as { error?: { message?: string } } | null)?.error?.message ??
        `HTTP ${res.status}`;
      const details = (parsed as { error?: { details?: Record<string, unknown> } } | null)
        ?.error?.details;
      throw new MigrationApiError(res.status, code, msg, details);
    }
    return parsed as T;
  }

  connect(api_token: string, selected_room_ids?: number[] | null): Promise<ConnectResponse> {
    return this.request<ConnectResponse>("POST", "/v1/migration/chatwork/connect", {
      api_token,
      selected_room_ids: selected_room_ids ?? null,
    });
  }

  preview(job_id: string): Promise<PreviewResponse> {
    return this.request<PreviewResponse>("POST", "/v1/migration/chatwork/preview", {
      job_id,
    });
  }

  start(job_id: string, selected_room_ids?: number[] | null): Promise<{ job_id: string; status: string }> {
    return this.request("POST", "/v1/migration/chatwork/start", {
      job_id,
      selected_room_ids: selected_room_ids ?? null,
    });
  }

  job(job_id: string): Promise<MigrationJobPublic> {
    return this.request<MigrationJobPublic>("GET", `/v1/migration/jobs/${job_id}`);
  }

  cancel(job_id: string): Promise<MigrationJobPublic> {
    return this.request<MigrationJobPublic>("POST", `/v1/migration/jobs/${job_id}/cancel`, {});
  }

  retryDeadLetters(job_id: string, dead_letter_ids?: string[]): Promise<{ retried: number }> {
    return this.request<{ retried: number }>(
      "POST",
      `/v1/migration/jobs/${job_id}/retry-deadletter`,
      { dead_letter_ids: dead_letter_ids ?? null },
    );
  }
}
